/**
 * MinigameModal - Container für alle Minispiele
 *
 * Lädt dynamisch die entsprechende Minispiel-Komponente basierend auf minigameId.
 * Verwaltet den Minispiel-Lifecycle und kommuniziert mit dem Server.
 * Unterstützt Tutorial-Phase für sequentielle Minispiele.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense, useSyncExternalStore } from 'react';
import './MinigameModal.css';
import socketService from '../../socketService';
import soundService from '../../soundService';
import { MINIGAME_EVENTS, MINIGAME_STATUS, MINIGAME_MODE } from '../../shared/core/minigameConstants';
import { getMinigameById } from '../../shared/entities/minigames';
import { MINIGAME_TIMINGS } from '../../shared/config/minigameTimings';
import { subscribe, getSnapshot, getServerSnapshot } from '../../state/minigameStore';

// Lazy-load minigame components
const minigameComponents = {
  placeholder: lazy(() => import('./games/PlaceholderMinigame')),
  reactionTest: lazy(() => import('./games/ReactionTestMinigame')),
  buttonMasher: lazy(() => import('./games/ButtonMasherMinigame')),
  geoQuiz: lazy(() => import('./games/GeoQuizMinigame')),
  mathQuiz: lazy(() => import('./games/MathQuizMinigame')),
  catchStick: lazy(() => import('./games/CatchStickMinigame')),
  musicQuiz: lazy(() => import('./games/MusicQuizMinigame')),
  missingCard: lazy(() => import('./games/MissingCardMinigame')),
  collisionCourse: lazy(() => import('./games/CollisionCourseMinigame')),
};

// Fallback loading component
function MinigameLoading() {
  return (
    <div className="minigame-loading">
      <div className="minigame-loading-spinner" />
      <p>Lade Minispiel...</p>
    </div>
  );
}

// Tutorial duration from shared config
const DEFAULT_TUTORIAL_DURATION = MINIGAME_TIMINGS.tutorialDuration;

export default function MinigameModal({
  minigameId,
  config,
  playerIndex,
  initialState,
  onComplete,
  players = [], // Array of player objects for sequential mode
}) {
  const [gameState, setGameState] = useState(initialState || {});
  const [status, setStatus] = useState(MINIGAME_STATUS.PLAYING);
  const [timeLeft, setTimeLeft] = useState(null);

  // Tutorial state
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialCountdown, setTutorialCountdown] = useState(DEFAULT_TUTORIAL_DURATION / 1000);

  // State from centralized store (Single Source of Truth)
  // Includes sequential mode state and synchronized tutorial timing
  const {
    sequential: sequentialState,
    tutorialStartTime: storeTutorialStartTime,
    tutorialDuration: storeTutorialDuration,
  } = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  // Track turn changes to reset status for new turns
  const prevTurnRef = useRef(sequentialState.turnNumber);

  // Get minigame definition
  const minigame = useMemo(() => getMinigameById(minigameId), [minigameId]);

  // Preload minigame sounds immediately when modal opens
  useEffect(() => {
    soundService.preload(['minigame_catch', 'minigame_woo', 'minigame_button', 'minigame_result']);
  }, []);

  // Check if this is a sequential minigame
  const isSequential = minigame?.mode === MINIGAME_MODE.SEQUENTIAL;

  // Tutorial duration from config prop, minigame definition, or default
  const tutorialDuration = config?.tutorialDuration || minigame?.config?.tutorialDuration || DEFAULT_TUTORIAL_DURATION;

  // Tutorial countdown effect - synchronized using server timestamp from store
  useEffect(() => {
    if (status !== MINIGAME_STATUS.PLAYING || !showTutorial || !minigame?.tutorial) {
      return;
    }

    // Use server-provided start time from store for synchronized countdown
    // This is set when all players complete the splash screen (STATE_UPDATE event)
    const serverStartTime = storeTutorialStartTime;
    const serverDuration = storeTutorialDuration || tutorialDuration;

    // Calculate how much time has already passed since server sent the play start
    const calculateRemaining = () => {
      if (serverStartTime) {
        const elapsed = Date.now() - serverStartTime;
        return Math.max(0, (serverDuration - elapsed) / 1000);
      }
      // Fallback to local timing if no server time available
      return tutorialDuration / 1000;
    };

    // Initialize with calculated remaining time
    setTutorialCountdown(calculateRemaining());

    const countdownInterval = setInterval(() => {
      const remaining = calculateRemaining();
      if (remaining <= 0.05) {
        clearInterval(countdownInterval);
        setShowTutorial(false);
        setTutorialCountdown(0);
      } else {
        setTutorialCountdown(remaining);
      }
    }, 100);

    return () => clearInterval(countdownInterval);
  }, [status, showTutorial, tutorialDuration, minigame?.tutorial, storeTutorialStartTime, storeTutorialDuration]);

  // Skip tutorial if no tutorial text defined
  useEffect(() => {
    if (!minigame?.tutorial && showTutorial) {
      setShowTutorial(false);
    }
  }, [minigame?.tutorial, showTutorial]);

  // Handle time up - just set status to completed
  // The minigame component will send its final score via onComplete
  const handleTimeUp = useCallback(() => {
    setStatus(MINIGAME_STATUS.COMPLETED);
    // Don't send score 0 here - let the component send its actual score
  }, []);

  // Set up timer if duration is specified (only after tutorial complete)
  useEffect(() => {
    if (!minigame?.duration) return;
    // For sequential minigames, don't start global timer (each turn has its own)
    if (isSequential) return;
    // Wait for tutorial to complete before starting timer
    if (showTutorial && minigame?.tutorial) return;

    const duration = config?.clickDuration || minigame.duration;
    setTimeLeft(Math.ceil(duration / 1000));

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [minigame, config, handleTimeUp, isSequential, showTutorial]);

  // Listen for server state updates
  useEffect(() => {
    const handleStateUpdate = (data) => {
      setGameState(prev => ({ ...prev, ...data }));
    };

    socketService.registerListener(MINIGAME_EVENTS.STATE_UPDATE, handleStateUpdate);

    return () => {
      socketService.off(MINIGAME_EVENTS.STATE_UPDATE, handleStateUpdate);
    };
  }, []);

  // Reset status when turn changes (sequential mode)
  // The store handles TURN_START events and updates sequentialState
  useEffect(() => {
    if (!isSequential) return;
    if (sequentialState.turnNumber > 0 && sequentialState.turnNumber !== prevTurnRef.current) {
      // New turn started - reset status to PLAYING
      setStatus(MINIGAME_STATUS.PLAYING);
      prevTurnRef.current = sequentialState.turnNumber;
    }
  }, [isSequential, sequentialState.turnNumber]);

  // Send action to server
  const sendAction = useCallback((action) => {
    socketService.emit(MINIGAME_EVENTS.PLAYER_ACTION, { action });
  }, []);

  // Handle player completing the minigame
  const handleComplete = useCallback((score) => {
    // Im Sequential Mode: Status NICHT auf COMPLETED setzen!
    // Das passiert erst wenn ALLE Spieler fertig sind (via RESULT event)
    if (!isSequential) {
      setStatus(MINIGAME_STATUS.COMPLETED);
    }
    socketService.emit(MINIGAME_EVENTS.PLAYER_COMPLETE, { score });
    onComplete?.({ score });
  }, [onComplete, isSequential]);

  // Allow clicking to skip tutorial (only in sequential mode for fairness)
  // In simultaneous mode, all players must wait the same time
  const handleTutorialClick = useCallback(() => {
    if (isSequential) {
      setShowTutorial(false);
    }
    // In simultaneous mode: do nothing, wait for countdown
  }, [isSequential]);

  // Get the component to render
  const MinigameComponent = minigameComponents[minigameId];

  if (!MinigameComponent) {
    return (
      <div className="minigame-modal">
        <div className="minigame-modal-content">
          <div className="minigame-error">
            <span className="error-icon">❓</span>
            <p>Unbekanntes Minispiel: {minigameId}</p>
          </div>
        </div>
      </div>
    );
  }

  // Determine effective status for the game component
  // During tutorial, the game is visible but doesn't receive 'playing' status
  const effectiveStatus = (showTutorial && minigame?.tutorial) ? 'tutorial' : status;

  // Add minigame-specific class for layout customization
  const modalClass = `minigame-modal minigame-${minigameId}`;
  const contentClass = `minigame-modal-content minigame-content-${minigameId}`;

  return (
    <div className={modalClass}>
      <div className="minigame-modal-backdrop" />

      <div className={contentClass}>
        {/* Header with timer and turn badge */}
        <div className="minigame-modal-header">
          <div className="minigame-header-info">
            <span className="minigame-header-icon">{minigame?.icon || '🎮'}</span>
            <span className="minigame-header-name">{minigame?.name || minigameId}</span>
          </div>

          <div className="minigame-header-right">
            {/* Sequential turn badge */}
            {isSequential && !showTutorial && (
              <div className={`minigame-turn-badge ${sequentialState.isMyTurn ? 'my-turn' : 'spectating'}`}>
                {sequentialState.isMyTurn ? 'Du bist dran!' : `${sequentialState.currentPlayerName || 'Spieler'} spielt...`}
                <span className="turn-count">({sequentialState.turnNumber}/{sequentialState.totalTurns})</span>
              </div>
            )}

            {timeLeft !== null && !showTutorial && (
              <div className={`minigame-timer ${timeLeft <= 5 ? 'warning' : ''}`}>
                <span className="timer-icon">⏱️</span>
                <span className="timer-value">{timeLeft}s</span>
              </div>
            )}
          </div>
        </div>

        {/* Minigame content area - game is visible behind tutorial */}
        <div className="minigame-game-area">
          <Suspense fallback={<MinigameLoading />}>
            <MinigameComponent
              config={config}
              playerIndex={playerIndex}
              gameState={gameState}
              status={effectiveStatus}
              sendAction={sendAction}
              onComplete={handleComplete}
              // Sequential mode props
              isMyTurn={isSequential ? sequentialState.isMyTurn : true}
              currentPlayerName={sequentialState.currentPlayerName}
              turnNumber={sequentialState.turnNumber}
              totalTurns={sequentialState.totalTurns}
            />
          </Suspense>

          {/* Tutorial Overlay - shown over the visible game */}
          {showTutorial && minigame?.tutorial && (
            <div className="tutorial-overlay" onClick={handleTutorialClick}>
              <div className="tutorial-content">
                <h3 className="tutorial-title">Anleitung</h3>
                <div className="tutorial-instructions">
                  {minigame.tutorial.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
                <div className="tutorial-countdown">{Math.ceil(tutorialCountdown)}</div>
                {isSequential && (
                  <div className="tutorial-skip-hint">
                    Tippe zum Überspringen
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
